import { Injectable } from '@angular/core';

import { forkJoin, of } from 'rxjs';
import { catchError, mergeMap, switchMap } from 'rxjs/operators';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { LoggerService } from '../../../core/services/logger.service';
import { SnackBarService } from '../../../core/services/snackbar.service';
import * as uiActions from '../../../modules/shared/store/ui.actions';
import { setCreatingChannel } from '../../../modules/shared/store/ui.actions';
import { IChannel, IChannelByParams } from '../interfaces/IChannel';
import { WorkspaceFeatures } from '../interfaces/IWorkspace';
import { ChannelsService } from '../services/channels.service';
import { MessagesService } from '../services/messages.service';
import { MessagingApiService } from '../services/messaging-api/messaging-api.service';
import { PermissionsService } from '../services/permissions.service';
import { TranslaterService } from '../services/translater.service';
import * as chatActions from './chat.actions';
import { AppStateWithChat } from './chat.reducer';
import { ChatEffectsBase } from './chat-effects-base';

@Injectable()
export class ChannelsEffects extends ChatEffectsBase {
  constructor(
    actions$: Actions,
    store: Store<AppStateWithChat>,
    messagingApiService: MessagingApiService,
    snackBarService: SnackBarService,
    channelsService: ChannelsService,
    messagesService: MessagesService,
    permissionsService: PermissionsService,
    logger: LoggerService,
    private translaterService: TranslaterService
  ) {
    super(
      actions$,
      store,
      messagingApiService,
      snackBarService,
      channelsService,
      messagesService,
      permissionsService,
      logger
    );
  }

  loadChannels$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.loadChannels),
      switchMap(({ autoSelectChannel, channelID, phoneNumber, concat, routing }) => {
        return forkJoin([this.messagingApiService.getChannels(), this.messagingApiService.getUsersByDefect()]).pipe(
          mergeMap((response) => {
            let channels = response[0];
            channels = this.channelsService.setChannelsOwnership(channels, response[1]);
            if (!channels.length) this.store.dispatch(uiActions.setAllChannelsLoaded({ value: true }));
            channels = this.channelsService.filterWhatsappChannels([...channels]);
            channels = this.channelsService.applyManualChannelsFilters(
              [...channels],
              this.channelsFilter.filters
            ) as IChannel[];
            channels = this.channelsService.sortChannels([...channels]);
            if (autoSelectChannel && channels?.length) {
              this.channelsService.autoSelectChannel(channels, channelID ?? '', phoneNumber ?? '', routing!);
            }
            this.store.dispatch(
              chatActions.setChannels({
                channels: channels,
                concat: concat,
              })
            );
            return [
              uiActions.setLoadingChannel({ value: false }),
              uiActions.setChannelsLoaded({ value: true }),
              uiActions.setLoadingChannels({ value: false }),
            ];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            this.snackBarService.showErrorSnackBar('Failed to get channels, please try again');
            return of(uiActions.setLoadingChannels({ value: false }), uiActions.setLoadingChannel({ value: false }));
          })
        );
      })
    )
  );

  getChannelLang$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.getChannelLang),
        mergeMap(({ channel }) => {
          return this.translaterService.getChannelLang(channel).pipe(
            mergeMap(({ lang }) => {
              if (lang && channel?.lang !== lang) {
                return [
                  chatActions.setChannelLang({
                    channelUID: channel.uid,
                    lang: lang,
                  }),
                ];
              }
              return [];
            }),
            catchError((error) => {
              this.logger.handleEffectError(error);
              return [];
            })
          );
        })
      ),
    { dispatch: true }
  );

  setChannelLang$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.setChannelLang),
        mergeMap(({ channelUID, lang }) => {
          return this.translaterService.setChannelLang(channelUID, lang).pipe(
            mergeMap(() => {
              return [];
            }),
            catchError((error) => {
              this.logger.handleEffectError(error);
              return [];
            })
          );
        })
      ),
    { dispatch: false }
  );

  pinChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.pinChannel),
        switchMap(({ channel, pinned }) => {
          return this.messagingApiService.pinChannel(channel, pinned).pipe(
            mergeMap(() => []),
            catchError((error) => {
              this.logger.handleEffectError(error);
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  changeBotMode$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.changeBotMode),
        switchMap(({ channel, value }) => {
          this.store.dispatch(uiActions.setLoadingChannel({ value: true }));
          return this.messagingApiService.changeBotMode(channel, value).pipe(
            mergeMap(() => {
              this.store.dispatch(uiActions.setLoadingChannel({ value: false }));
              this.store.dispatch(chatActions.setBotMode({ channel, value }));
              return [];
            }),
            catchError((error) => {
              this.store.dispatch(uiActions.setLoadingChannel({ value: false }));
              this.snackBarService.showErrorSnackBar(`Can't change to bot mode`);
              this.logger.handleEffectError(error);
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  changeChannelEnabledStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.changeChannelEnabledStatus),
        switchMap(({ channel, enabled }) => {
          return this.messagingApiService.changeChannelEnabledStatus(channel, enabled).pipe(
            mergeMap(() => {
              this.store.dispatch(chatActions.setChannelEnabledStatus({ channel, enabled }));
              return [];
            }),
            catchError((error) => {
              this.logger.handleEffectError(error);
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  changeChannelOrderId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.changeChannelOrderId),
      switchMap(({ channel, orderId }) => {
        return this.messagingApiService.changeChannelOrderID(channel, orderId).pipe(
          mergeMap(() => [chatActions.setChannelOrderId({ channel, orderId })]),
          catchError((error) => {
            this.logger.handleEffectError(error);
            return of();
          })
        );
      })
    )
  );

  setChannelInfo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.setChannelInfo),
        switchMap(({ channel, displayName }) => {
          this.store.dispatch(
            chatActions.getStandardReplies({ channel: { ...channel, standardResponses: [], displayName: displayName } })
          );
          return this.messagingApiService.patchChannelInfo(channel, displayName).pipe(
            mergeMap(() => []),
            catchError((error) => {
              this.logger.handleEffectError(error);
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  changeChannelPhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.changeChannelPhone),
      switchMap(({ channel, phoneNumber, oldPhone }) => {
        return this.messagingApiService.patchChannelPhone(channel, phoneNumber, oldPhone).pipe(
          mergeMap(() => {
            return [chatActions.setChannelPhone({ channel, phoneNumber, oldPhone })];
          }),
          catchError((error) => {
            this.logger.handleEffectError(error);
            this.snackBarService.showErrorSnackBar('Failed to change phone Nnumber');
            return of();
          })
        );
      })
    )
  );

  createPhoneChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.createChannelByParams),
      switchMap(({ channelByParams, autoSelect }) => {
        this.store.dispatch(setCreatingChannel({ value: true }));
        return this.messagingApiService.createChannelByParams(channelByParams).pipe(
          mergeMap((channel) => {
            if (!channel) {
              return of(uiActions.setCreatingChannel({ value: false }), uiActions.setLoadingChannel({ value: false }));
            }
            channel.standardResponses = [];
            const updatedChannel = this.channelsService.setChannelsOwnership([channel], this.users)[0];
            if (!updatedChannel) {
              return of(uiActions.setCreatingChannel({ value: false }), uiActions.setLoadingChannel({ value: false }));
            }
            channel = updatedChannel;
            if (autoSelect) {
              this.channelsService.selectChannel(channel);
              if (channelByParams?.temporaryDisplayName) {
                this.store.dispatch(
                  chatActions.setChannelDisplayName({ channel, displayName: channelByParams.temporaryDisplayName })
                );
              }
            }
            return of(uiActions.setCreatingChannel({ value: false }), uiActions.setLoadingChannel({ value: false }));
          }),
          catchError((error) => {
            this.logger.handleEffectError(error);
            this.snackBarService.showErrorSnackBar('Failed to create channel, please try again');
            return of(uiActions.setCreatingChannel({ value: false }), uiActions.setLoadingChannel({ value: false }));
          })
        );
      })
    )
  );

  muteChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.muteChannel),
        switchMap(({ channel, mute }) => {
          return this.messagingApiService.muteChannel(channel, mute).pipe(
            mergeMap(() => []),
            catchError((error) => {
              this.logger.handleEffectError(error);
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  changeChannelStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.changeChannelStatus),
      switchMap(({ channel, newStatus, oldStatus }) => {
        this.store.dispatch(chatActions.setChannelStatus({ value: newStatus, channel: channel }));
        return this.messagingApiService.changeChannelStatus(channel, newStatus).pipe(
          mergeMap(() => []),
          catchError((error) => {
            this.logger.handleEffectError(error);
            this.snackBarService.showErrorSnackBar("Channel Status can't change");
            return of(chatActions.setChannelStatus({ value: oldStatus, channel: channel }));
          })
        );
      })
    )
  );

  changeChannelOwner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.changeChannelOwner),
      switchMap(({ channel, owner }) => {
        this.store.dispatch(
          chatActions.setChannelOwner({
            channel,
            owner,
            tags: this.channelsService.modifyTagsOwner(channel, owner.id, owner.displayName),
          })
        );
        return this.messagingApiService.changeChannelOwner(channel, owner).pipe(
          mergeMap(() => {
            return [chatActions.getStandardReplies({ channel: { ...channel, owner: owner } })];
          }),
          catchError((error) => {
            this.logger.handleEffectError(error);
            this.snackBarService.showErrorSnackBar("Channel owner can't change");
            return of();
          })
        );
      })
    )
  );

  loadDealers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.loadDealers),
      switchMap(() => {
        return this.messagingApiService.getDealers().pipe(
          mergeMap((dealers) => [chatActions.setDealers({ dealers })]),
          catchError((error) => {
            this.logger.handleEffectError(error);
            return [chatActions.unsetDealers()];
          })
        );
      })
    )
  );

  selectDealer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.selectDealer),
      switchMap(({ dealer }) => {
        return this.messagingApiService.selectDealer(dealer).pipe(
          mergeMap(() => [chatActions.unsetDealers(), chatActions.loadDealers()]),
          catchError((error) => {
            this.logger.handleEffectError(error);
            return of();
          })
        );
      })
    )
  );

  changeOPT$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.changeOPT),
      switchMap(({ enabled }) => {
        const channelUID = this.selectedChannel?.uid ?? '';
        return this.messagingApiService.optIntOutState(enabled).pipe(
          mergeMap(() => [
            chatActions.setChannelStatusEnabled({ value: enabled, channelUID }),
            chatActions.setLoadingMessages({ channelUID, value: false }),
          ]),
          catchError((error) => {
            this.logger.handleEffectError(error);
            if (!channelUID) {
              return of();
            }
            return of(chatActions.setChannelStatusEnabled({ value: !enabled, channelUID }));
          })
        );
      })
    )
  );

  getChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getChannel),
      switchMap(({ channelUID, autoSelect, channelByParams, updateChannel, tryCreateChannel, ignoreTags }) => {
        const params = channelByParams ?? ({} as IChannelByParams);
        return forkJoin([
          this.messagingApiService.getChannel(channelUID ?? '', params, ignoreTags ?? false),
          this.messagingApiService.getUsersByDefect(),
        ]).pipe(
          mergeMap((response) => {
            let channel: IChannel = response[0];
            const lastMessage = this.messagesService.getLastMessage(channel.messages);
            if (channelByParams?.tags?.length && !ignoreTags) channel.messages = [];
            channel = this.channelsService.setDatesMessages(channel, params);
            channel = this.channelsService.setChannelsOwnership([channel], response[1])[0];
            if (autoSelect) this.channelsService.selectChannel(channel);
            let channels = [...this.channels];
            const indexChannel = channels.map((e) => e.uid).indexOf(channel.uid);
            if (indexChannel !== -1) channels[indexChannel] = channel;
            if (this.channelsService.isFilterChannel(channel, this.channelsFilter.filters)) {
              channels = [...channels, channel];
            }
            this.store.dispatch(chatActions.setChannels({ channels, concat: false }));
            if (updateChannel && channelByParams) {
              this.channelsService.updateChannelByParams(channel, channelByParams);
            }
            if (channelByParams?.startDateTime) {
              this.store.dispatch(
                chatActions.getChannelOldMessagesValue({ channel, startDate: channelByParams.startDateTime })
              );
            }

            if (channelByParams?.temporaryDisplayName) {
              this.store.dispatch(
                chatActions.setChannelDisplayName({ channel, displayName: channelByParams.temporaryDisplayName })
              );
            }
            if (!channelByParams?.tags?.length || ignoreTags) {
              return [uiActions.setLoadingChannel({ value: false }), uiActions.setLoadingChannels({ value: false })];
            } else if (lastMessage) {
              return [chatActions.getMessagesByTagMessage({ channel, message: lastMessage })];
            }
            return [uiActions.setLoadingChannel({ value: false }), uiActions.setLoadingChannels({ value: false })];
          }),
          catchError((error) => {
            this.logger.handleEffectError(error);
            if (tryCreateChannel && error.status === 404 && channelByParams) {
              if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canTryCreateChannelByPhone])) {
                return of(chatActions.createChannelByParams({ channelByParams, autoSelect }));
              }
            }
            this.snackBarService.showErrorSnackBar('Failed to get channel, please try again');
            return of(uiActions.setLoadingChannel({ value: false }), chatActions.unsetChannel());
          })
        );
      })
    )
  );

  changeReadChannelStatus$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.changeReadChannelStatus),
        mergeMap(({ channel, value }) => {
          this.store.dispatch(chatActions.setReadChannel({ channel, value }));
          return this.messagingApiService.setReadChannel(channel, value).pipe(
            mergeMap(() => {
              return [];
            }),
            catchError((err) => {
              this.logger.handleEffectError(err);
              return [];
            })
          );
        })
      ),
    { dispatch: false }
  );

  readAllMessages$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.readAllMessages),
        mergeMap(() => {
          this.store.dispatch(chatActions.setAllChannelsRead());
          return this.messagingApiService.readAllMessages().pipe(
            mergeMap(() => {
              return [];
            }),
            catchError((err) => {
              this.logger.handleEffectError(err);
              return [];
            })
          );
        })
      ),
    { dispatch: false }
  );

  deleteMemberChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.deleteMemberInChannel),
        switchMap(({ members }) => {
          return this.messagingApiService.deleteMemberChannel(members).pipe(
            mergeMap(() => {
              return [];
            }),
            catchError((err) => {
              this.logger.handleEffectError(err);
              this.snackBarService.showErrorSnackBar('Failed to delete member, please try again');
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  addMembersChannel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(chatActions.addMembersChannel),
        switchMap(({ members }) => {
          return this.messagingApiService.addMembersChannel(members).pipe(
            mergeMap(() => {
              return [];
            }),
            catchError((err) => {
              this.logger.handleEffectError(err);
              this.snackBarService.showErrorSnackBar('Failed to add member, please try again');
              return of();
            })
          );
        })
      ),
    { dispatch: false }
  );

  leaveChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.leaveChannel),
      switchMap(({ channelUID }) => {
        this.store.dispatch(uiActions.setLoadingChannel({ value: true }));
        this.store.dispatch(chatActions.toggleSidebar({ active: false }));
        return this.messagingApiService.leaveChannel().pipe(
          mergeMap(() => {
            return [
              chatActions.leaveChannelMember({ channelUID: channelUID }),
              uiActions.setLoadingChannel({ value: false }),
            ];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            this.snackBarService.showErrorSnackBar('Failed to leave channel, please try again');
            return [uiActions.setLoadingChannel({ value: false })];
          })
        );
      })
    )
  );

  joinChannel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.joinChannel),
      switchMap(({ channelUID }) => {
        return this.messagingApiService.joinChannel(channelUID).pipe(
          mergeMap(() => {
            return [chatActions.getChannel({ channelUID: channelUID, autoSelect: true })];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            this.snackBarService.showErrorSnackBar('Failed to join, please try again');
            return [uiActions.setLoadingChannel({ value: false })];
          })
        );
      })
    )
  );

  getChannelMembersPhotos$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getChannelMembersPhotos),
      mergeMap(({ channel }) => {
        return this.messagingApiService.getChannelMembersPhotos(channel).pipe(
          mergeMap((members) => {
            return [chatActions.setChannelMembersPhotos({ members: members, channelUID: channel.uid })];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            return [];
          })
        );
      })
    )
  );

  getChannelOldMessagesValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(chatActions.getChannelOldMessagesValue),
      mergeMap(({ channel, startDate }) => {
        return this.messagingApiService.getChannelOldMessagesValue(channel, startDate).pipe(
          mergeMap((value) => {
            return [chatActions.setChannelOldMessagesValue({ value, channel })];
          }),
          catchError((err) => {
            this.logger.handleEffectError(err);
            return [];
          })
        );
      })
    )
  );
}
